import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Invoice } from '@otw/models'
import { capResults } from 'hooks/search/filterSearchItems'
import {
  fetchInvoices,
  SEARCH_CACHE_STALE_MS,
  SEARCH_QUERY_KEYS,
} from 'hooks/search/searchCaches'

interface UseInvoiceSearchOptions {
  searchTerm: string
  enabled?: boolean
}

export interface InvoiceSearchResult {
  invoice: Invoice
  leadId?: string
  leadName?: string
}

const isRetailInvoice = (inv: Invoice): boolean =>
  Boolean(
    inv.template_name?.startsWith('Retail Order') ||
      inv.template_name === 'Invoice' ||
      inv.template_name === 'Odometer Statement',
  )

export function filterInvoicesByTerm(
  invoices: Invoice[],
  searchTerm: string,
): InvoiceSearchResult[] {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return []
  }

  const trimmedTerm = searchTerm.trim().toLowerCase()
  const matching = invoices.filter((inv) => {
    if (!isRetailInvoice(inv)) return false
    const invoiceNumber = inv.invoice_number?.toLowerCase() || ''
    const clientName = inv.client_name?.toLowerCase() || ''
    const clientEmail = inv.client_email?.toLowerCase() || ''
    const leadName = inv.lead_name?.toLowerCase() || ''
    return (
      invoiceNumber.includes(trimmedTerm) ||
      clientName.includes(trimmedTerm) ||
      clientEmail.includes(trimmedTerm) ||
      leadName.includes(trimmedTerm)
    )
  })

  return capResults(matching, 16).map((inv) => ({
    invoice: inv,
    leadId: inv.lead_id,
    leadName: inv.lead_name,
  }))
}

export const useInvoiceSearch = ({
  searchTerm,
  enabled = true,
}: UseInvoiceSearchOptions) => {
  const { data: invoices = [], isPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.invoices,
    queryFn: fetchInvoices,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })

  const data = useMemo(
    () => filterInvoicesByTerm(invoices, searchTerm),
    [invoices, searchTerm],
  )

  return {
    data,
    isLoading: isPending && invoices.length === 0,
  }
}
